import { useEffect, useState } from "react";

const getInitialMatch = (query: string): boolean => {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }
  return window.matchMedia(query).matches;
};

/**
 * Generic `window.matchMedia` subscription hook — mirrors `useIsDesktop.ts`'s
 * pattern (SSR-safe initial read, `addEventListener`/`addListener` fallback
 * for Safari <14) but for an arbitrary media query string instead of one
 * hardcoded query. Backs the PMA-COCK-106 landscape-tablet rail breakpoint
 * (`(max-width: 1180px) and (orientation: landscape)`) in `LoggerCockpit.tsx`.
 * The PMA-COCK-109 smartphone-block threshold used to be a `matchMedia`
 * query through this hook too, but was moved to a `window.screen`-based
 * check (`useIsDeviceTooSmall` in `LoggerCockpit.tsx`) — a viewport query
 * here can't distinguish a real device from a viewport shrunk by browser
 * chrome, which is what let an iPad in landscape slip under the old
 * threshold. See `LoggerCockpit.tsx`'s `useIsDeviceTooSmall` doc comment for
 * the full history.
 *
 * The `query` string MUST stay a literal/stable value at each call site (not
 * rebuilt on every render) — a changing query re-subscribes on every render,
 * which still works but is wasteful. Callers in this codebase pass a module-
 * level constant, not an inline template string.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => getInitialMatch(query));

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }
    const mql = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };
    setMatches(mql.matches);

    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handleChange);
      return () => mql.removeEventListener("change", handleChange);
    }
    // Safari <14 fallback
    mql.addListener(handleChange);
    return () => mql.removeListener(handleChange);
  }, [query]);

  return matches;
}
